import React, { useState } from 'react';
import { UTCDate } from '@date-fns/utc';
import { addYears } from 'date-fns';
import { Button, OutlineButton } from '../../components/display/button';
import { useForm, FormProvider as Form } from 'react-hook-form';
import { calcNumUTCDays } from 'shared/src/utils/date/date-utils';
import { toUTCDate } from 'shared/src/date-string-utils/date-utils';
import { useCampaignContext } from '@/features/campaigns/campaign-context';
import { zodResolver } from '@hookform/resolvers/zod';
import { StrategyApiUpdateRequest } from 'shared/src/api/strategy-api-types';
import { useProposalContext } from '@/features/proposals/proposal-context';
import { trpc } from '@/utils/trpc-client';
import {
  calculatedProposalValues,
  DateField,
  formatCurrSafe,
  formatDateSafe,
  formatPctSafe,
  InputShell,
  NumDays,
  PctInput,
  PriceInput,
  shouldShowAgency,
  shouldShowRefRate,
  ViewField
} from '@/features/proposals/strategy-form-utils';

export function ProposalAttributes() {
  const [editable, setEditable] = useState<boolean>(false);
  return (
    <div className="w-full flex justify-end">
      <div className="flex items-center justify-between  px-1 py-0.5 mb-2">
        {editable ? (
          <InputForm setEditable={setEditable} />
        ) : (
          <ViewMode setEditable={setEditable} />
        )}
      </div>
    </div>
  );
}

type InputFormProps = {
  setEditable: (editable: boolean) => void;
};

function InputForm({ setEditable }: InputFormProps) {
  const { proposal } = useProposalContext();
  const { price, start_date, end_date, referral_commission_rate, agency_markup_rate } = proposal;
  const { mutateAsync } = trpc.updateProposal.useMutation();
  const utils = trpc.useUtils();

  const form = useForm<StrategyApiUpdateRequest>({
    resolver: zodResolver(StrategyApiUpdateRequest),
    defaultValues: {
      price: price ?? undefined,
      start_date: start_date ?? undefined,
      end_date: end_date ?? undefined,
      referral_commission_rate: referral_commission_rate ?? undefined,
      agency_markup_rate: agency_markup_rate ?? undefined
    },
    mode: 'all'
  });

  async function submitChanges(data: StrategyApiUpdateRequest) {
    const isDirty = !!Object.keys(form.formState.dirtyFields).length;
    if (isDirty) {
      await mutateAsync({ proposal_id: proposal.id, ...data });
      await utils.getProposal.invalidate();
      await utils.proposalDiff.invalidate();
    } else {
      form.reset();
    }
    setEditable(false);
  }

  const values = form.watch();
  const { numDays, referralFee, effectivePrice, agencyMarkupAmount, grossPrice } =
    calculatedProposalValues(values);

  const { control, formState, handleSubmit } = form;
  return (
    <Form {...form}>
      <form noValidate onSubmit={handleSubmit(submitChanges)} className="flex flex-col gap-1">
        <div className="flex gap-1">
          <Button size="compact" type="submit" disabled={formState.isSubmitting}>
            Done
          </Button>
          <InputShell name="price" label="Price">
            <PriceInput control={control} />
          </InputShell>
          <InputShell name="start_date" label="Start">
            <DateField
              min={new UTCDate('2020-01-01')}
              max={values.end_date ? new UTCDate(values.end_date) : addYears(new UTCDate(), 10)}
              name="start_date"
            />
          </InputShell>
          <InputShell name="end_date" label="End">
            <DateField
              min={values.start_date ? new UTCDate(values.start_date) : new UTCDate('2020-01-01')}
              max={addYears(new UTCDate(), 10)}
              name="end_date"
            />
          </InputShell>
          <NumDays numDays={numDays} />
        </div>
        <div className="flex w-full items-center justify-end">
          <div className="flex gap-1">
            <InputShell name="referral_commission_rate" label="Ref. Rate">
              <PctInput name="referral_commission_rate" control={control} />
            </InputShell>
            <ViewField label="Eff. Price" value={formatCurrSafe(effectivePrice)} />
            <ViewField label="Ref. Fee" value={formatCurrSafe(referralFee)} />
          </div>
        </div>
        <div className="flex w-full items-center justify-end">
          <div className="flex gap-1">
            <InputShell name="agency_markup_rate" label="Agency %">
              <PctInput name="agency_markup_rate" control={control} />
            </InputShell>
            <ViewField label="Markup Amt." value={formatCurrSafe(agencyMarkupAmount)} />
            <ViewField label="Gross Price" value={formatCurrSafe(grossPrice)} />
          </div>
        </div>
      </form>
    </Form>
  );
}

type ViewModeProps = {
  setEditable: (editable: boolean) => void;
};

function ViewMode({ setEditable }: ViewModeProps) {
  const { proposal } = useProposalContext();
  const { campaign } = useCampaignContext();
  const { start_date, end_date } = proposal;
  const numDays =
    start_date && end_date ? calcNumUTCDays(toUTCDate(start_date), toUTCDate(end_date)) : '-';
  const showRefRate = shouldShowRefRate(proposal, campaign);
  const showAgency = shouldShowAgency(proposal, campaign);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-1">
        <OutlineButton className="bg-transparent font-normal" onClick={() => setEditable(true)}>
          Edit
        </OutlineButton>
        <ViewField label="Price" value={formatCurrSafe(proposal.price)} />
        <ViewField label="Start" value={formatDateSafe(proposal.start_date)} />
        <ViewField label="End" value={formatDateSafe(proposal.end_date)} />
        <NumDays numDays={numDays} />
      </div>
      {showRefRate && (
        <div className="flex w-full items-center justify-end">
          <div className="flex gap-1">
            <ViewField label="Ref. Rate" value={formatPctSafe(proposal.referral_commission_rate)} />
            <ViewField label="Eff. Price" value={formatCurrSafe(proposal.effective_price)} />
            <ViewField
              label="Ref. Fee"
              value={formatCurrSafe(proposal.referral_commission_amount)}
            />
          </div>
        </div>
      )}
      {showAgency && (
        <div className="flex w-full items-center justify-end">
          <div className="flex gap-1">
            <ViewField label="Agency %" value={formatPctSafe(proposal.agency_markup_rate)} />
            <ViewField label="Markup Amt." value={formatCurrSafe(proposal.agency_markup_amount)} />
            <ViewField label="Gross Price" value={formatCurrSafe(proposal.gross_price)} />
          </div>
        </div>
      )}
    </div>
  );
}
